import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { PAYLOAD_TYPES } from "@/lib/constants";
import { LAYERS } from "@/lib/layers";
import { ProvenanceBadge, Mono, InfoNote, copyText } from "@/components/uikit";
import { toast } from "sonner";
import { Anchor, Copy, Loader2 } from "lucide-react";

export function AnchorDialog({ open, onOpenChange }) {
  const { anchorEvent } = useApp();
  const [payload, setPayload] = useState("");
  const [type, setType] = useState("Test Payload");
  const [layer, setLayer] = useState("1");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setPayload("");
    setType("Test Payload");
    setLayer("1");
    setNotes("");
    setResult(null);
  };

  const sample = () => {
    setPayload(
      JSON.stringify(
        { event: "sample-capture", source: "console-operator", value: 42, note: "demo payload" },
        null,
        2
      )
    );
    setType("Digital Event");
  };

  const submit = async () => {
    if (!payload.trim()) {
      toast.error("Payload required", { description: "Enter or generate a payload before anchoring." });
      return;
    }
    setBusy(true);
    try {
      const rec = await anchorEvent({
        payload,
        payload_type: type,
        layer: Number(layer),
        notes,
        source_tag: "local",
      });
      setResult(rec);
      toast.success("Event anchored", { description: `Local SHA-256 evidence created — ${rec.event_id}` });
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-[#0D121D] text-slate-200 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading uppercase tracking-tight text-slate-100">
            <Anchor className="h-5 w-5 text-cyan-400" aria-hidden="true" /> Anchor Event
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Capture → Canonicalize → Hash → Anchor. Produces a locally verifiable SHA-256 evidence record.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label htmlFor="anchor-payload" className="text-slate-300">Payload</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-cyan-400 hover:text-cyan-300" onClick={sample} data-testid="anchor-sample-button">
                  Generate sample
                </Button>
              </div>
              <Textarea
                id="anchor-payload"
                data-testid="anchor-payload-input"
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                rows={6}
                placeholder="Paste an event, document, AI response, telemetry object, or system state…"
                className="border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-slate-300">Payload type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="anchor-type-select" className="border-slate-700 bg-slate-950 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                    {PAYLOAD_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-300">ADA-4WM layer</Label>
                <Select value={layer} onValueChange={setLayer}>
                  <SelectTrigger data-testid="anchor-layer-select" className="border-slate-700 bg-slate-950 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 border-slate-700 bg-slate-900 text-slate-200">
                    {LAYERS.map((l) => (
                      <SelectItem key={l.n} value={String(l.n)}>L{l.n} — {l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="anchor-notes" className="mb-1.5 block text-slate-300">Operator notes</Label>
              <Input
                id="anchor-notes"
                data-testid="anchor-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context / mission notes"
                className="border-slate-700 bg-slate-950 text-slate-200"
              />
            </div>

            <InfoNote tone="info">
              This creates <strong>LOCAL cryptographic evidence</strong> only. It is not blockchain, RFC 3161, or
              third-party notarization.
            </InfoNote>
          </div>
        ) : (
          <div className="space-y-3">
            <InfoNote tone="ok">Event anchored. Record appended to the local evidence chain.</InfoNote>
            <ProvenanceBadge source="local" />
            <ReceiptRow label="event_id" value={result.event_id} />
            <ReceiptRow label="timestamp" value={result.timestamp} />
            <ReceiptRow label="sha256" value={result.sha256} copyable />
            <ReceiptRow label="previous_hash" value={result.previous_hash} />
            <div>
              <p className="mb-1 font-mono text-[11px] uppercase tracking-wider text-slate-500">canonical_payload</p>
              <pre className="max-h-40 overflow-auto rounded border border-slate-800 bg-slate-950 p-2 font-mono text-[11px] text-cyan-200">{result.canonical_payload}</pre>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!result ? (
            <>
              <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => handleClose(false)} data-testid="anchor-cancel-button">
                Cancel
              </Button>
              <Button onClick={submit} disabled={busy} className="bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="anchor-submit-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Anchor className="mr-2 h-4 w-4" />}
                Anchor Event
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="border-slate-700 text-slate-300" onClick={reset} data-testid="anchor-another-button">
                Anchor another
              </Button>
              <Button onClick={() => handleClose(false)} className="bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="anchor-done-button">
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptRow({ label, value, copyable }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-slate-800 bg-slate-950/60 px-3 py-1.5">
      <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="flex items-center gap-1.5">
        <Mono className="max-w-[220px] truncate sm:max-w-[360px]">{value}</Mono>
        {copyable && (
          <button
            type="button"
            aria-label={`Copy ${label}`}
            className="text-slate-500 hover:text-cyan-400"
            onClick={() => copyText(value, "Copied hash")}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
